import React, { useState, useRef, useMemo, useEffect } from 'react';
import { FiSearch, FiCheck } from 'react-icons/fi';

// =============================================
// Searchable, keyboard-navigable multi-select list.
//   - type to filter (key strokes)
//   - ↑/↓ move highlight, PageUp/PageDown jump, Home/End ends
//   - Enter toggles the highlighted row's selection (stays in the field)
//   - Tab leaves to the next field (default browser behavior — not trapped)
// Items keep their given order; an optional `group` renders section headers.
// =============================================

export interface PickItem { key: string; label: string; group?: string; sub?: string; }

interface Props {
  items: PickItem[];
  selected: string[];
  onToggle: (key: string) => void;
  placeholder?: string;
  emptyText?: string;
  ariaLabel?: string;
  maxHeight?: number;
}

const SearchPickList: React.FC<Props> = ({
  items, selected, onToggle, placeholder = 'Search…',
  emptyText = 'Nothing to show', ariaLabel = 'Searchable list', maxHeight = 240,
}) => {
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? items.filter(i => i.label.toLowerCase().includes(t)) : items;
  }, [items, q]);

  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${hi}"]`) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setHi(h => Math.min(filtered.length - 1, h + 10)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setHi(h => Math.max(0, h - 10)); }
    else if (e.key === 'Home') { e.preventDefault(); setHi(0); }
    else if (e.key === 'End') { e.preventDefault(); setHi(filtered.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = filtered[hi]; if (it) onToggle(it.key); }
    // Tab: let the browser move focus to the next field (not trapped)
  };

  // Group rows in given order while tracking each row's flat index (for highlight).
  let idx = -1;
  const order: string[] = [];
  const byGroup = new Map<string, { item: PickItem; i: number }[]>();
  filtered.forEach(it => {
    idx++; const g = it.group || '';
    if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
    byGroup.get(g)!.push({ item: it, i: idx });
  });

  return (
    <div className="spl">
      <div className="spl-search">
        <FiSearch size={13} />
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder={placeholder} aria-label={ariaLabel} />
      </div>
      <div className="spl-list" ref={listRef} style={{ maxHeight }}>
        {filtered.length === 0 ? (
          <div className="spl-empty">{emptyText}</div>
        ) : order.map(g => (
          <div key={g || '_'}>
            {g && <div className="spl-group">{g}</div>}
            {byGroup.get(g)!.map(({ item, i }) => {
              const on = selected.includes(item.key);
              return (
                <div
                  key={item.key}
                  data-idx={i}
                  className={`spl-row ${i === hi ? 'hi' : ''} ${on ? 'on' : ''}`}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => onToggle(item.key)}
                >
                  <span className="spl-check">{on && <FiCheck size={13} />}</span>
                  <span className="spl-label">{item.label}{item.sub && <span className="spl-sub"> · {item.sub}</span>}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {selected.length > 0 && <div className="spl-count">{selected.length} selected</div>}
    </div>
  );
};

export default SearchPickList;
